import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { bookData } from '../data/bookData';

const HomePage = () => {
  const [marketingCopy, setMarketingCopy] = useState({});
  const alteredSeries = Object.entries(bookData).filter(([_, book]) => book.series === 'The Altered');
  const upcomingBooks = Object.entries(bookData).filter(([_, book]) => book.status === 'unpublished');

  useEffect(() => {
    const fetchMarketingCopy = async () => {
      const copyPromises = Object.entries(bookData).map(async ([key, book]) => {
        if (book.marketingCopyUrl) {
          try {
            const response = await fetch(book.marketingCopyUrl);
            const text = await response.text();
            return [key, text];
          } catch (error) {
            console.error(`Error fetching marketing copy for ${key}:`, error);
            return [key, ''];
          }
        }
        return [key, ''];
      });

      const copyEntries = await Promise.all(copyPromises);
      setMarketingCopy(Object.fromEntries(copyEntries));
    };

    fetchMarketingCopy();
  }, []);

  const truncateHtml = (html, maxLength) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    let truncated = '';
    let currentLength = 0;

    const walkTheDOM = (node) => {
      if (currentLength >= maxLength) return;
      
      if (node.nodeType === Node.TEXT_NODE) {
        const remainingLength = maxLength - currentLength;
        const text = node.textContent.slice(0, remainingLength);
        truncated += text;
        currentLength += text.length;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        truncated += `<${node.tagName.toLowerCase()}>`;
        for (let child of node.childNodes) {
          walkTheDOM(child);
        }
        truncated += `</${node.tagName.toLowerCase()}>`;
      }
    };

    walkTheDOM(div);
    return currentLength >= maxLength ? truncated + '...' : truncated;
  };

  return (
    <>
      <Helmet>
        <title>Blou Bryant - Thrillers with a Twist of Tomorrow</title>
        <meta name="description" content="Explore the thrilling world of Blou Bryant's sci-fi novels. Discover The Altered series featuring cutting-edge science, AI, and genetic engineering in high-stakes action adventures." />
        <link rel="canonical" href="https://www.bloubryant.com" />
        <meta property="og:title" content="Blou Bryant - Thrillers with a Twist of Tomorrow" />
        <meta property="og:description" content="Explore the thrilling world of Blou Bryant's sci-fi novels. Discover The Altered series featuring cutting-edge science, AI, and genetic engineering in high-stakes action adventures." />
        <meta property="og:url" content="https://www.bloubryant.com" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="space-y-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="text-center">
          <h1 className="text-5xl font-bold mb-2">Blou Bryant</h1>
          <p className="text-2xl italic mb-4">Thrillers with a Twist of Tomorrow</p>
          <blockquote className="text-xl">"brilliant writing... There is a new talent in town." - Viking Jam</blockquote>
          <blockquote className="text-xl">"The storyline was excellent... a high octane SciFi thriller with bold characterization and fast paced action." - Amazon Review</blockquote>
        </section>

        <section className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-3xl font-semibold mb-4">Who is Blou Bryant?</h2>
          <p className="text-lg">
            Blou Bryant could be anyone. He could be your next-door neighbor. Or, perhaps he's a recluse, a hermit, or a monk. That's a tad cliche, boring even, so instead, how about we assume he's a former spy, living under an assumed name in a location he's not allowed to share? 
            Perhaps he looks like the love child of Idris Elba and Brad Pitt. He could. It's possible. Everything is possible, and the beauty of mystery is in the not knowing. 
            So why not imagine the author you'd like me to be? After all, the best stories happen in your mind.
          </p>
        </section>

        <section className="bg-gray-100 p-6 rounded-lg">
          <h2 className="text-3xl font-semibold mb-4">The Altered Series</h2>
          <p className="text-lg mb-4">
            Step into a world where cutting edge science, AI, and genetic engineering meet high-stakes action. 
            Follow Wyatt Miller as he navigates a landscape of homicidal billionaires, shadowy forces, bike gangs, 
            and wanna-be vampires in adventures that won't give him - or you - a chance to take a breath.
          </p>
          <div className="my-4"></div>
          <p className="text-lg">
            Read or listen to the first 3 chapters for free - click on Catalyst to start. Only 2.99$ US to buy the audio or ebook for the first 1000 purchases!
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {alteredSeries.map(([key, book]) => (
              <div key={key} className="bg-white p-4 rounded shadow flex flex-col">
                <Link to={`/books/${key}`}>
                  <h3 className="text-2xl font-semibold mb-2">{book.title}</h3>
                </Link>
                <div className="flex-grow flex flex-col justify-between">
                  {book.imageUrl && (
                    <Link to={`/books/${key}`}>
                      <img 
                        src={book.imageUrl} 
                        alt={`Cover of ${book.title}, book in The Altered series by Blou Bryant`} 
                        className="w-full h-48 object-contain mb-4" 
                      />
                    </Link>
                  )}
                  <div 
                    className="mb-4 flex-grow"
                    dangerouslySetInnerHTML={{ __html: truncateHtml(marketingCopy[key], 200) }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {upcomingBooks.length > 0 && (
          <section className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-3xl font-semibold mb-4">Coming Soon</h2>
            <div className="space-y-4">
              {upcomingBooks.map(([key, book]) => (
                <div key={key}>
                  <h3 className="text-xl font-semibold">
                    <Link to={`/books/${key}`} className="text-blue-600 hover:underline">
                      {book.title}
                    </Link>
                  </h3>
                  <div 
                    dangerouslySetInnerHTML={{ __html: truncateHtml(marketingCopy[key], 200) }}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="bg-gray-100 p-6 rounded-lg">
          <h2 className="text-3xl font-semibold mb-4">Reader Praise</h2>
          <blockquote className="italic mb-4">
            "I loved the book. Fresh, exciting and a quick read." - Goodread review
          </blockquote>
          <blockquote className="italic mb-4">
            "A well-written, exciting debut thriller... Highly recommendable!" - The Columbia Review
          </blockquote>
          <blockquote className="italic">
            "Blou Bryant has produced one of the most unusual, yet gripping thrillers I have ever read. 
            Each new page brings a shocking new discovery, with every piece of the puzzle leaving more questions" - Rosie - Goodreads
          </blockquote>
        </section>

        <section className="text-center">
          <p className="text-lg">
            Got questions or want to know more? (Really, you do? Why?),{' '}
            <Link to="/about" className="text-blue-600 hover:underline">
              Visit About Blou Bryant
            </Link>.
          </p>
        </section>
      </div>
    </>
  );
};

export default HomePage;